import React, { useRef, useEffect, useCallback } from "react";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import HoldAndWinProgressBarController from "./HoldAndWinProgressBarController";
import HoldAndWinProgressBarTensionOverlay from "./HoldAndWinProgressBarTensionOverlay";
import { safeInit } from "../../../utilities";
import {
  selectHoldAndWinActive,
  selectHoldAndWinCurrentRound,
  selectHoldAndWinSpinComplete,
} from "../../../State/selectors";
import { useHoldAndWinControl } from "../../../Contexts/HoldAndWinControlContext";

/**
 * HoldAndWinProgressBar - Displays coin collection progress for Hold and Win feature
 * Shows animated progress from 1-20 coins with orientation-specific animations
 */
const HoldAndWinProgressBar = () => {
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const holdAndWinCurrentRound = useSelector(selectHoldAndWinCurrentRound);
  const holdAndWinSpinComplete = useSelector(selectHoldAndWinSpinComplete);

  const progressBarControllerRef = useRef(null);
  const progressBarContainerRef = useRef(null);
  const tensionOverlayRef = useRef(null);
  const prevProgressRef = useRef(null);
  const localProgressCountRef = useRef(null); // Track progress during spin

  const { registerProgressBarCallback } = useHoldAndWinControl();

  const dimensionsContext = useDimensions();
  const { dimensions, symbolWidth, symbolHeight, device, orientation } =
    dimensionsContext;

  // Get progress bar value from storage (default to 6 if not available)
  const progressBarValue = holdAndWinCurrentRound?.storage?.progress_bar || 6;

  // Callback when a coin lands - increment progress immediately
  const handleCoinLanded = useCallback(() => {
    if (localProgressCountRef.current !== null) {
      localProgressCountRef.current += 1;

      if (progressBarControllerRef.current) {
        // Update progress display (controller handles tension stop at 15/20)
        progressBarControllerRef.current.updateProgress(
          localProgressCountRef.current,
          orientation
        );
        prevProgressRef.current = localProgressCountRef.current;

        // Handle tension start at milestones (stop is handled by controller)
        const currentProgress = localProgressCountRef.current;

        if (currentProgress === 14) {
          tensionOverlayRef.current?.playTensionIn(15, orientation);
        } else if (currentProgress === 19) {
          tensionOverlayRef.current?.playTensionIn(20, orientation);
        }
      }
    }
  }, [orientation]);

  // Initialize controller when container is ready and Hold and Win becomes active
  useEffect(() => {
    if (
      progressBarContainerRef.current &&
      !progressBarControllerRef.current &&
      holdAndWinActive
    ) {
      safeInit(() => {
        progressBarControllerRef.current = new HoldAndWinProgressBarController(
          progressBarContainerRef.current,
          dimensions,
          symbolWidth,
          symbolHeight,
          device,
          orientation
        );

        // Pass tension overlay ref to progress bar controller
        progressBarControllerRef.current.setTensionOverlayRef(tensionOverlayRef);

        // Show progress bar immediately after initialization
        requestAnimationFrame(() => {
          if (
            progressBarControllerRef.current &&
            progressBarControllerRef.current.initialized &&
            progressBarControllerRef.current.portraitSpine &&
            progressBarControllerRef.current.landscapeSpine
          ) {
            if (progressBarValue !== undefined && progressBarValue !== null) {
              progressBarControllerRef.current.updateProgress(
                progressBarValue,
                orientation
              );
              prevProgressRef.current = progressBarValue;

              // CRITICAL: On reload, if we're at 14 or 19, start tension animation
              // This handles the case where player reloads during tension state
              if (progressBarValue === 14) {
                console.log("[HoldAndWinProgressBar] Reload at progress 14, starting tension for milestone 15");
                tensionOverlayRef.current?.playTensionIn(15, orientation);
              } else if (progressBarValue === 19) {
                console.log("[HoldAndWinProgressBar] Reload at progress 19, starting tension for milestone 20");
                tensionOverlayRef.current?.playTensionIn(20, orientation);
              }
            } else {
              progressBarControllerRef.current.showProgressBar();
            }
          }
        });
      });
    }
  }, [
    holdAndWinActive,
    dimensions,
    symbolWidth,
    symbolHeight,
    device,
    orientation,
    progressBarValue,
  ]);

  // Handle dimension changes
  useEffect(() => {
    if (
      progressBarControllerRef.current &&
      symbolWidth &&
      symbolHeight &&
      dimensions
    ) {
      progressBarControllerRef.current.updateDimensions(
        dimensions,
        symbolWidth,
        symbolHeight,
        device,
        orientation
      );
    }
  }, [symbolWidth, symbolHeight, dimensions, device, orientation]);

  // Update progress bar when value changes - ONLY after spin is complete
  useEffect(() => {
    if (
      progressBarControllerRef.current &&
      holdAndWinActive &&
      holdAndWinSpinComplete &&
      progressBarValue !== undefined &&
      progressBarValue !== null &&
      prevProgressRef.current !== progressBarValue
    ) {
      // Only update if local progress hasn't already reached this value
      // (prevents jump back if coins landed incrementally)
      progressBarControllerRef.current.updateProgress(
        progressBarValue,
        orientation
      );
      prevProgressRef.current = progressBarValue;
    }
  }, [progressBarValue, holdAndWinSpinComplete, holdAndWinActive, orientation]);

  // Handle orientation changes immediately (don't wait for spin complete)
  useEffect(() => {
    if (
      progressBarControllerRef.current &&
      holdAndWinActive &&
      progressBarControllerRef.current.currentOrientation !== orientation
    ) {
      progressBarControllerRef.current.updateProgress(
        progressBarValue,
        orientation
      );
    }
  }, [orientation, holdAndWinActive, progressBarValue]);

  // Register callback for coin landings
  useEffect(() => {
    const unregister = registerProgressBarCallback(handleCoinLanded);
    return unregister; // Cleanup function removes this specific callback
  }, [registerProgressBarCallback, handleCoinLanded]);

  // Initialize/reset local progress tracking based on spin state
  useEffect(() => {
    if (!holdAndWinSpinComplete && holdAndWinActive) {
      // Spin is happening - initialize local progress if not already set
      if (localProgressCountRef.current === null) {
        localProgressCountRef.current = prevProgressRef.current || 0;
      }
    }
    // DON'T reset localProgressCountRef when spin completes - we need it for the 20th coin callback
    // It will be reset when Hold and Win becomes inactive
  }, [holdAndWinSpinComplete, holdAndWinActive]);

  // Reset previous progress when Hold and Win becomes inactive
  useEffect(() => {
    if (!holdAndWinActive) {
      prevProgressRef.current = null;
      localProgressCountRef.current = null;
    }
  }, [holdAndWinActive]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (progressBarControllerRef.current) {
        progressBarControllerRef.current.cleanup();
      }
    };
  }, []);

  if (!dimensions || !dimensions.width || !dimensions.height) {
    return null;
  }

  return (
    <>
      <Container
        name="hold_and_win_progress_bar"
        x={0}
        y={0}
        sortableChildren={true}
        zIndex={10}
        ref={progressBarContainerRef}
        visible={holdAndWinActive}
      />
      {holdAndWinActive && (
        <Container
          name="hold_and_win_progress_bar_tension_container"
          x={0}
          y={0}
          sortableChildren={true}
          zIndex={11}
          visible={holdAndWinActive}
        >
          <HoldAndWinProgressBarTensionOverlay
            ref={tensionOverlayRef}
            dimensions={dimensions}
            symbolWidth={symbolWidth}
            symbolHeight={symbolHeight}
            device={device}
            orientation={orientation}
          />
        </Container>
      )}
    </>
  );
};

export default HoldAndWinProgressBar;
